import { useState, useCallback, useEffect, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Trash } from 'react-feather';
import type { CategoryGroup } from '../types';

type ModalComponent = React.ComponentType<{
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, colors: string[], categoryId: number, paletteId?: number) => void;
  initialColors?: string[];
  initialName?: string;
  categories: CategoryGroup[];
  defaultCategoryId?: number;
  disableCategorySelection?: boolean;
  mode?: 'create' | 'edit';
  paletteId?: number;
  ModalComponent: ModalComponent;
};

const DEFAULT_COLORS = [
  '#4D7FB3',
  '#DE674F',
  '#47C5C5',
  '#6784CC',
  '#B7D97C',
];

const MIN_COLORS = 5;
const MAX_COLORS = 25;

export const PaletteCreationModal = ({ 
  open, 
  onClose, 
  onSave,
  initialColors = DEFAULT_COLORS,
  initialName = '',
  categories,
  defaultCategoryId,
  disableCategorySelection = false,
  mode = 'create',
  paletteId,
  ModalComponent
}: Props) => {
  const [paletteName, setPaletteName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(
    defaultCategoryId || categories[0]?.category.id || 1
  );
  const [colors, setColors] = useState<string[]>(initialColors);
  const [colorInputValues, setColorInputValues] = useState<Record<number, string>>({});

  // Update selected category when modal opens or defaultCategoryId changes
  useEffect(() => {
    if (open && defaultCategoryId) {
      setSelectedCategoryId(defaultCategoryId);
    }
  }, [open, defaultCategoryId]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialName) {
        setPaletteName(initialName);
      }
      setColors(initialColors);
      setColorInputValues({});
    }
  }, [open, mode, initialName, initialColors]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPaletteName(e.target.value);
  }, []);

  const handleColorInputChange = useCallback((index: number, value: string) => {
    setColorInputValues(prev => ({
      ...prev,
      [index]: value,
    }));
  }, []);

  const validateAndUpdateColor = useCallback((index: number, value: string) => {
    // Remove # if present, convert to uppercase
    const cleanValue = value.replace('#', '').toUpperCase();
    
    // Check if valid hex
    if (/^[0-9A-F]{6}$/i.test(cleanValue)) {
      const hexColor = `#${cleanValue}`;
      setColors(prev => {
        const updated = [...prev];
        updated[index] = hexColor;
        return updated;
      });
      // Clear input value state to use the validated color
      setColorInputValues(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return hexColor;
    }
    return null;
  }, []);

  const handleColorBlur = useCallback((index: number) => {
    const inputValue = colorInputValues[index];
    if (inputValue !== undefined) {
      const validated = validateAndUpdateColor(index, inputValue);
      if (!validated) {
        // Invalid - revert to current color
        setColorInputValues(prev => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    }
  }, [colorInputValues, validateAndUpdateColor]);

  const handleAddColor = useCallback(() => {
    if (colors.length < MAX_COLORS) {
      const lastColor = colors[colors.length - 1];
      setColors(prev => [...prev, lastColor]);
    }
  }, [colors]);

  const handleRemoveColor = useCallback((index: number) => {
    if (colors.length > MIN_COLORS) {
      setColors(prev => prev.filter((_, i) => i !== index));
      // Clean up any input state for this color
      setColorInputValues(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  }, [colors.length]);

  const handleSave = useCallback(() => {
    if (!paletteName.trim()) {
      return;
    }
    if (mode === 'edit' && paletteId) {
      onSave(paletteName.trim(), colors, selectedCategoryId, paletteId);
    } else {
      onSave(paletteName.trim(), colors, selectedCategoryId);
    }
    handleCancel();
  }, [paletteName, colors, selectedCategoryId, mode, paletteId, onSave]);

  const handleCancel = useCallback(() => {
    setPaletteName('');
    setSelectedCategoryId(defaultCategoryId || categories[0]?.category.id || 1);
    setColors(initialColors);
    setColorInputValues({});
    onClose();
  }, [initialColors, defaultCategoryId, categories, onClose]);

  const canSave = paletteName.trim().length > 0 && colors.length >= MIN_COLORS;
  const canRemove = colors.length > MIN_COLORS;

  // Split colors into two columns
  const columnLength = Math.ceil(MAX_COLORS / 2);
  const leftColumn = colors.slice(0, columnLength);
  const rightColumn = colors.slice(columnLength);

  return (
    <ModalComponent open={open} onClose={handleCancel}>
      <ModalRoot>
        <ModalWrap>
          <ModalHeader>Edit Template</ModalHeader>
          
          <ModalMain>
            <NameInputContainer>
              <NameInput
                type="text"
                value={paletteName}
                onChange={handleNameChange}
                placeholder="Theme name"
                autoFocus
              />
            </NameInputContainer>

            <PaletteContainer>
              <PaletteScroll>
                <PaletteWrap>
                  <PaletteMain>
                    {/* Left Column */}
                    <PaletteColumn>
                      {leftColumn.map((color, i) => (
                        <ColorItem key={i}>
                          <ColorInputWrap>
                            <ColorSwatch style={{ backgroundColor: color }} />
                            <ColorInputMain>
                              <ColorInputPrefix>#</ColorInputPrefix>
                              <ColorInputField
                                type="text"
                                value={colorInputValues[i] ?? color.replace('#', '').toUpperCase()}
                                onChange={(e) => handleColorInputChange(i, e.target.value)}
                                onBlur={() => handleColorBlur(i)}
                                maxLength={6}
                              />
                            </ColorInputMain>
                          </ColorInputWrap>
                          {canRemove && (
                            <RemoveButton onClick={() => handleRemoveColor(i)}>
                              <Trash size={18} />
                            </RemoveButton>
                          )}
                        </ColorItem>
                      ))}
                    </PaletteColumn>

                    {/* Right Column */}
                    <PaletteColumn>
                      {rightColumn.map((color, i) => {
                        const actualIndex = i + columnLength;
                        return (
                          <ColorItem key={actualIndex}>
                            <ColorInputWrap>
                              <ColorSwatch style={{ backgroundColor: color }} />
                              <ColorInputMain>
                                <ColorInputPrefix>#</ColorInputPrefix>
                                <ColorInputField
                                  type="text"
                                  value={colorInputValues[actualIndex] ?? color.replace('#', '').toUpperCase()}
                                  onChange={(e) => handleColorInputChange(actualIndex, e.target.value)}
                                  onBlur={() => handleColorBlur(actualIndex)}
                                  maxLength={6}
                                />
                              </ColorInputMain>
                            </ColorInputWrap>
                            {canRemove && (
                              <RemoveButton onClick={() => handleRemoveColor(actualIndex)}>
                                <Trash size={18} />
                              </RemoveButton>
                            )}
                          </ColorItem>
                        );
                      })}
                    </PaletteColumn>
                  </PaletteMain>
                </PaletteWrap>
              </PaletteScroll>
            </PaletteContainer>

            {colors.length < MAX_COLORS && (
              <AddColorContainer>
                <AddColorButton onClick={handleAddColor}>
                  + Add Color
                </AddColorButton>
              </AddColorContainer>
            )}

            <ButtonSet>
              <CancelButton onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SaveButton onClick={handleSave} disabled={!canSave}>
                Save
              </SaveButton>
            </ButtonSet>
          </ModalMain>
        </ModalWrap>
      </ModalRoot>
    </ModalComponent>
  );
};

PaletteCreationModal.displayName = 'PaletteCreationModal';

// Styled Components matching Modal.Editor design
const ModalRoot = styled.div`
  display: flex;
  min-height: 100%;
  padding: 32px;
  box-sizing: border-box;
  width: 600px;
  max-width: 100vw;
`;

const ModalWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  width: 100%;
`;

const ModalMain = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  min-height: 500px;
  max-height: 80vh;
`;

const ModalHeader = styled.h2`
  margin: 0 0 32px 0;
  font-family: var(--font-bold);
  font-size: 22px;
  color: var(--black, #1a1a1a);
  text-align: center;
`;

const NameInputContainer = styled.div`
  margin-bottom: 20px;
  height: 40px;
  flex-shrink: 0;
`;

const NameInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 10px 15px;
  border: 1px solid var(--gray-l, #ddd);
  border-radius: 4px;
  font-size: 16px;
  font-family: var(--font-reg, inherit);
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--gray-m, #999);
  }
  
  &::placeholder {
    color: var(--gray-l, #ccc);
  }
`;

const PaletteContainer = styled.div`
  flex: 1;
  margin-bottom: 20px;
  overflow: hidden;
  min-height: 300px;
`;

const PaletteScroll = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow-y: auto;
  height: 100%;
  padding-right: 8px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const PaletteWrap = styled.div`
  width: 100%;
`;

const PaletteMain = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const PaletteColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ColorItem = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
  
  &:hover > button {
    display: block;
  }
`;

const ColorInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
`;

const ColorSwatch = styled.div`
  display: flex;
  height: 41px;
  width: 41px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const ColorInputMain = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 5px 10px 15px;
  border: 1px solid var(--gray-l, #ddd);
  border-radius: 4px;
  box-sizing: border-box;
  cursor: text;
  flex: 1;
  
  &:focus-within {
    border-color: var(--gray-m, #999);
  }
`;

const ColorInputPrefix = styled.span`
  font-size: 16px;
  color: #333;
`;

const ColorInputField = styled.input`
  font-family: var(--font-reg, inherit);
  border: none;
  font-size: 16px;
  width: 4.5em;
  padding-right: 5px;
  text-transform: uppercase;
  background: transparent;
  
  &:focus {
    outline: none;
  }
  
  &::selection {
    background-color: var(--blue-l, #b3d9ff);
  }
`;

const RemoveButton = styled.button`
  display: none;
  color: var(--gray-l, #ccc);
  margin: 0 5px;
  transition: color 150ms ease-in-out;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 4px;
  
  &:hover {
    color: var(--gray, #999);
  }
`;

const AddColorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 45px;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const AddColorButton = styled.button`
  min-width: 200px;
  height: 100%;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-bold);
  font-size: 16px;
  color: var(--black-l, #333);
  transition: all 150ms ease-in-out;
  
  &:hover {
    background-color: var(--gray-xl, #f5f5f5);
    color: var(--black-l, #000);
  }
  
  &:active {
    background-color: var(--blue-xl, #e6f2ff);
  }
`;

const ButtonSet = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 45px;
  gap: 12px;
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 20px;
`;

const CancelButton = styled.button`
  width: 150px;
  height: 100%;
  font-family: var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  font-size: 16px;
  text-align: center;
  cursor: pointer;
  border-radius: 5px;
  border: 1px solid var(--gray, #999);
  color: var(--gray-d, #666);
  background-color: var(--pri-01, white);
  transition: background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in;

  &:hover {
    background-color: var(--gray-xl, #f5f5f5);
  }

  &:disabled {
    border-color: var(--gray-l, #ddd);
    color: var(--pri-01, white);
    background-color: var(--gray-l, #ddd);
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  width: 150px;
  height: 100%;
  font-family: var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  font-size: 16px;
  text-align: center;
  cursor: pointer;
  border-radius: 5px;
  border: 2px solid var(--pri-02, #4A90E2);
  background-color: var(--pri-02, #4A90E2);
  color: var(--pri-01, white);
  transition: background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in;

  &:hover {
    border: 2px solid var(--sec-02, #357ABD);
    background-color: var(--sec-02, #357ABD);
  }

  &:disabled {
    border-color: var(--gray-l, #ddd);
    color: var(--pri-01, white);
    background-color: var(--gray-l, #ddd);
    cursor: not-allowed;
  }
`;
